import React, { type ReactNode } from "react";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  BellRing,
  Code2,
  LoaderCircle,
  Mail,
  MessageSquareText,
  Sparkles,
  Search,
  Filter,
  ArrowUpDown
} from "lucide-react";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Input } from "../../components/ui/input";

const API = import.meta.env.VITE_API_URL || "http://localhost:9000";

type VariableSpec = {
  key: string;
  label: string;
  description?: string;
};

type MessageTemplate = {
  id: string;
  eventId: string;
  groupId: string;
  groupName: string;
  channel: "email" | "sms" | "in_app";
  category: "transactional" | "notification";
  name: string;
  description: string;
  channelActive: boolean;
  variables: VariableSpec[];
  exampleVariables: Record<string, unknown>;
  subjectTemplate?: string;
  htmlTemplate?: string;
  smsTemplate?: string;
  notificationTemplate?: string;
  preview: {
    subject?: string;
    html?: string;
    message?: string;
  };
};

type TemplateGroup = {
  id: string;
  name: string;
  category: "transactional" | "notification";
  templates: MessageTemplate[];
};

export function MeldingsmalerPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [templates, setTemplates] = useState<MessageTemplate[]>([]);
  const [selectedId, _setSelectedId] = useState<string | null>(searchParams.get("template"));

  const setSelectedId = (id: string | null) => {
    _setSelectedId(id);
    if (id) {
      setSearchParams({ template: id }, { replace: true });
    } else {
      setSearchParams({}, { replace: true });
    }
  };
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [draftSubjectTemplate, setDraftSubjectTemplate] = useState("");
  const [draftHtmlTemplate, setDraftHtmlTemplate] = useState("");
  const [draftSmsTemplate, setDraftSmsTemplate] = useState("");
  const [draftNotificationTemplate, setDraftNotificationTemplate] = useState("");
  const [draftChannelActive, setDraftChannelActive] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    void fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API}/api/admin/message-templates`, {
        credentials: "include",
      });
      if (!response.ok) {
        throw new Error("Kunne ikke hente meldingsmaler.");
      }

      const data = (await response.json()) as MessageTemplate[];
      setTemplates(data);
      _setSelectedId((current) => {
        if (current && data.some((t: MessageTemplate) => t.id === current)) return current;
        return data[0]?.id ?? null;
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Kunne ikke hente meldingsmaler.");
    } finally {
      setLoading(false);
    }
  };

  const groupedTemplates = templates.reduce<TemplateGroup[]>((groups, template) => {
    const existing = groups.find((group) => group.id === template.groupId);
    if (existing) {
      existing.templates.push(template);
      return groups;
    }

    groups.push({
      id: template.groupId,
      name: template.groupName,
      category: template.category,
      templates: [template],
    });
    return groups;
  }, []);

  groupedTemplates.forEach((group) => {
    group.templates.sort((a, b) => {
      const order = { email: 0, sms: 1, in_app: 2 };
      return order[a.channel] - order[b.channel];
    });
  });

  const filteredGroups = groupedTemplates.filter(group =>
    group.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    group.templates.some(t => t.description.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const selectedTemplate =
    templates.find((template) => template.id === selectedId) ?? templates[0] ?? null;
  const selectedGroup =
    groupedTemplates.find((group) => group.id === selectedTemplate?.groupId) ?? groupedTemplates[0] ?? null;
  const selectedVariant =
    selectedGroup?.templates.find((template) => template.id === selectedId) ?? selectedGroup?.templates[0] ?? null;

  const transactionalCount = templates.filter((template) => template.category === "transactional").length;
  const notificationCount = templates.filter((template) => template.category === "notification").length;

  useEffect(() => {
    if (!selectedVariant) return;
    setDraftSubjectTemplate(selectedVariant.subjectTemplate ?? "");
    setDraftHtmlTemplate(selectedVariant.htmlTemplate ?? "");
    setDraftSmsTemplate(selectedVariant.smsTemplate ?? "");
    setDraftNotificationTemplate(selectedVariant.notificationTemplate ?? "");
    setDraftChannelActive(selectedVariant.channelActive);
    setSaveError(null);
  }, [selectedVariant?.id]);

  const saveTemplate = async () => {
    if (!selectedVariant) return;

    setSaving(true);
    setSaveError(null);
    try {
      const payload = selectedVariant.channel === "email"
        ? {
            emailEnabled: draftChannelActive,
            emailSubjectTemplate: draftSubjectTemplate,
            emailHtmlTemplate: draftHtmlTemplate,
          }
        : selectedVariant.channel === "in_app"
          ? {
              inAppEnabled: draftChannelActive,
              notificationTemplate: draftNotificationTemplate,
            }
          : {
              smsEnabled: draftChannelActive,
              smsTemplate: draftSmsTemplate,
            };

      const response = await fetch(`${API}/api/admin/message-templates/${selectedVariant.eventId}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error("Kunne ikke lagre malen.");
      }

      await fetchTemplates();
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : "Kunne ikke lagre malen.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-1">
        <h1 className="text-2xl font-bold tracking-tight text-slate-900">Meldingsmaler</h1>
        <p className="text-slate-500 text-sm">
          En samlet visning av alle systemmeldinger i kodebasen.
        </p>
      </div>

      {loading ? (
        <div className="flex h-[400px] items-center justify-center">
          <LoaderCircle className="h-8 w-8 animate-spin text-primary/20" />
        </div>
      ) : error ? (
        <div className="rounded-xl border border-destructive/20 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          {error}
        </div>
      ) : (
        <>
          <div className="grid gap-6 lg:grid-cols-[380px_1fr]">
            {/* Sidebar / List */}
            <div className="space-y-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Søk i maler..."
                  className="pl-10 bg-white border-slate-200"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>

              <div className="space-y-2 overflow-y-auto pr-2 custom-scrollbar">
                {filteredGroups.map((group) => {
                  const isActive = group.id === selectedGroup?.id;
                  return (
                    <button
                      key={group.id}
                      onClick={() => setSelectedId(group.templates[0]?.id ?? null)}
                      className={`w-full text-left p-4 rounded-xl border transition-all ${isActive
                        ? "bg-slate-900 border-slate-900 text-white shadow-md ring-2 ring-slate-900/10"
                        : "bg-white border-slate-200 text-slate-900 hover:border-slate-300 hover:bg-slate-50"
                        }`}
                    >
                      <div className="flex justify-between items-start mb-2">
                        <span className="font-bold tracking-tight">{group.name}</span>
                        <Badge variant={isActive ? "secondary" : "muted"} className={isActive ? "bg-white/10 text-white border-transparent" : ""}>
                          {group.category}
                        </Badge>
                      </div>
                      <p className={`text-xs line-clamp-2 mb-3 ${isActive ? "text-slate-300" : "text-slate-500"}`}>
                        {group.templates[0]?.description}
                      </p>
                      <div className="flex gap-1.5">
                        {group.templates.map((t) => (
                          <div
                            key={t.id}
                            className={`flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${isActive ? "bg-white/10 text-white" : "bg-slate-100 text-slate-500"
                              }`}
                          >
                            {t.channel === "email" ? (
                              <Mail className="h-3 w-3" />
                            ) : t.channel === "in_app" ? (
                              <BellRing className="h-3 w-3" />
                            ) : (
                              <MessageSquareText className="h-3 w-3" />
                            )}
                            {t.channel === "in_app" ? "in-app" : t.channel}
                          </div>
                        ))}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Detail View */}
            <div className="space-y-6">
              {selectedGroup && selectedVariant ? (
                <Card className="border-slate-200/60 shadow-sm overflow-hidden">
                  <header className="px-6 py-5 border-b border-slate-100 bg-slate-50/30 flex justify-between items-start gap-4">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <Badge className="bg-blue-500/10 text-blue-600 border-blue-500/20 capitalize">
                          {selectedGroup.category}
                        </Badge>
                        <span className="text-slate-300">•</span>
                        <span className="text-xs font-mono text-slate-400">{selectedVariant.id}</span>
                      </div>
                      <h2 className="text-xl font-bold text-slate-900">{selectedGroup.name}</h2>
                    </div>
                    <div className="text-right">
                      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Variabler</p>
                      <p className="text-2xl font-black text-slate-900">{selectedVariant.variables.length}</p>
                    </div>
                  </header>

                  <div className="p-6 space-y-8">
                    {/* Channel Selector */}
                    <Tabs value={selectedVariant.id} onValueChange={setSelectedId} className="w-full">
                      <TabsList className="bg-slate-100 p-1 rounded-xl">
                        {selectedGroup.templates.map((template) => (
                          <TabsTrigger
                            key={template.id}
                            value={template.id}
                            className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm px-4"
                          >
                            {template.channel === "email" ? (
                              <Mail className="h-3.5 w-3.5 mr-2 text-slate-500" />
                            ) : template.channel === "in_app" ? (
                              <BellRing className="h-3.5 w-3.5 mr-2 text-slate-500" />
                            ) : (
                              <MessageSquareText className="h-3.5 w-3.5 mr-2 text-slate-500" />
                            )}
                            {template.channel === "email" ? "E-post" : template.channel === "in_app" ? "In-app varsel" : "SMS"}
                          </TabsTrigger>
                        ))}
                      </TabsList>
                    </Tabs>

                    <div className="grid gap-8 lg:grid-cols-[1fr_320px]">
                      {/* Main Preview */}
                      <div className="space-y-6">
                        {selectedVariant.preview.subject && (
                          <PreviewBlock title="E-post Emne" icon={<Mail />}>
                            <div className="p-4 rounded-xl bg-white border border-slate-200 font-semibold text-slate-900">
                              {selectedVariant.preview.subject}
                            </div>
                          </PreviewBlock>
                        )}

                        {selectedVariant.preview.html && (
                          <PreviewBlock title="HTML Forhåndsvisning" icon={<Sparkles />}>
                            <div
                              className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm overflow-hidden prose prose-sm max-w-none shadow-[inset_0_1px_4px_rgba(0,0,0,0.02)]"
                              dangerouslySetInnerHTML={{ __html: selectedVariant.preview.html }}
                            />
                          </PreviewBlock>
                        )}

                        {selectedVariant.preview.message && (
                          <PreviewBlock title={selectedVariant.channel === "in_app" ? "In-app Varsel" : "SMS Melding"} icon={selectedVariant.channel === "in_app" ? <BellRing /> : <MessageSquareText />}>
                            <div className={selectedVariant.channel === "in_app" 
                              ? "rounded-2xl border border-border bg-white p-5 text-sm leading-relaxed text-foreground shadow-sm max-w-md flex items-start gap-3"
                              : "rounded-2xl bg-slate-900 p-5 text-sm leading-relaxed text-white shadow-xl max-w-md"
                            }>
                              {selectedVariant.channel === "in_app" && <BellRing className="h-4 w-4 text-primary mt-0.5" />}
                              <div className="flex-1">
                                {selectedVariant.preview.message}
                              </div>
                            </div>
                          </PreviewBlock>
                        )}

                        <PreviewBlock title="Rediger Mal" icon={<Code2 />}>
                          <div className="rounded-2xl border border-slate-200 bg-white p-5 space-y-4">
                            <label className="flex items-center gap-3 text-sm font-medium text-slate-700">
                              <input
                                type="checkbox"
                                checked={draftChannelActive}
                                onChange={(e) => setDraftChannelActive(e.target.checked)}
                                className="h-4 w-4 rounded border-slate-300 text-slate-900"
                              />
                              Kanal aktiv
                            </label>

                              {selectedVariant.channel === "email" ? (
                                <>
                                  <div className="space-y-2">
                                    <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">Emnemal</p>
                                    <Input value={draftSubjectTemplate} onChange={(e) => setDraftSubjectTemplate(e.target.value)} />
                                  </div>
                                  <div className="space-y-2">
                                    <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">HTML-mal</p>
                                    <textarea
                                      value={draftHtmlTemplate}
                                      onChange={(e) => setDraftHtmlTemplate(e.target.value)}
                                      className="min-h-[260px] w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm"
                                    />
                                  </div>
                                </>
                              ) : selectedVariant.channel === "in_app" ? (
                                <div className="space-y-2">
                                  <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">In-app varsel-mal</p>
                                  <textarea
                                    value={draftNotificationTemplate}
                                    onChange={(e) => setDraftNotificationTemplate(e.target.value)}
                                    className="min-h-[180px] w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm"
                                  />
                                </div>
                              ) : (
                                <div className="space-y-2">
                                  <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">SMS-mal</p>
                                  <textarea
                                    value={draftSmsTemplate}
                                    onChange={(e) => setDraftSmsTemplate(e.target.value)}
                                    className="min-h-[180px] w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm"
                                  />
                                </div>
                              )}

                            {saveError ? (
                              <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600">
                                {saveError}
                              </div>
                            ) : null}

                            <div className="flex justify-end">
                              <Button onClick={() => void saveTemplate()} disabled={saving}>
                                {saving ? "Lagrer..." : "Lagre mal"}
                              </Button>
                            </div>
                          </div>
                        </PreviewBlock>
                      </div>

                      {/* Variables & Examples */}
                      <div className="space-y-6">
                        <div className="space-y-4">
                          <h3 className="flex items-center gap-2 text-[11px] font-bold text-slate-400 uppercase tracking-widest">
                            <Code2 className="h-3.5 w-3.5" />
                            Eksempeldata
                          </h3>
                          <div className="space-y-2">
                            {Object.entries(selectedVariant.exampleVariables).map(([key, value]) => (
                              <div key={key} className="p-3 rounded-xl bg-slate-50 border border-slate-100">
                                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-tighter mb-0.5">{key}</p>
                                <p className="text-xs font-mono text-blue-600 truncate">{String(value)}</p>
                              </div>
                            ))}
                          </div>
                        </div>

                        <div className="space-y-4">
                          <h3 className="flex items-center gap-2 text-[11px] font-bold text-slate-400 uppercase tracking-widest">
                            <BellRing className="h-3.5 w-3.5" />
                            Tilgjengelige Variabler
                          </h3>
                          <div className="space-y-3">
                            {selectedVariant.variables.length === 0 ? (
                              <p className="text-xs text-slate-400 italic">Ingen variabler definert.</p>
                            ) : (
                              selectedVariant.variables.map((v) => (
                                <div key={v.key} className="p-4 rounded-xl border border-slate-100 bg-white shadow-sm">
                                  <p className="text-xs font-bold text-slate-900 mb-1">{v.label}</p>
                                  <code className="text-[11px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded font-mono">{`{{${v.key}}}`}</code>
                                  {v.description && (
                                    <p className="mt-2 text-[11px] text-slate-500 leading-relaxed">{v.description}</p>
                                  )}
                                </div>
                              ))
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </Card>
              ) : (
                <div className="h-96 flex items-center justify-center rounded-2xl border border-dashed border-slate-200 text-slate-400">
                  Velg en mal for å se detaljer
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function PreviewBlock({
  title,
  icon,
  children,
}: {
  title: string;
  icon: React.ReactElement<{ className?: string }>;
  children: ReactNode;
}) {
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 text-[11px] font-bold text-slate-400 uppercase tracking-widest">
        {React.cloneElement(icon, { className: "h-3.5 w-3.5" })}
        {title}
      </div>
      {children}
    </div>
  );
}
